import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { PageHeader, Badge, Btn, Table, inputCls } from "../../components/admin/AdminUI";
import { EmailEditor } from "../../components/admin/EmailEditor";
import { useAdmin, can } from "../../components/admin/AdminLayout";

const TemplateSheet = ({ id, onClose, onChanged }) => {
  const admin = useAdmin();
  const edit = can(admin, "manage_templates");
  const [t, setT] = useState(null);
  const [versions, setVersions] = useState([]);
  const [to, setTo] = useState(admin.email);
  const load = async () => {
    const [a, b] = await Promise.all([api.get(`/admin/email/templates/${id}`), api.get(`/admin/email/templates/${id}/versions`)]);
    setT(a.data); setVersions(b.data);
  };
  useEffect(() => { if (id) load().catch((e) => toast.error(errMsg(e))); else setT(null); }, [id]); // eslint-disable-line
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const save = () => act(() => api.put(`/admin/email/templates/${id}`, { name: t.name, subject: t.subject, preview_text: t.preview_text, html_body: t.html_body, text_body: t.text_body }), "Saved as new draft version");
  const dup = () => act(() => api.post(`/admin/email/templates/${id}/duplicate`), "Template duplicated");
  const approve = () => act(() => api.post(`/admin/email/templates/${id}/approve`), "Template approved");
  const test = () => act(() => api.post(`/admin/email/templates/${id}/test`, { to }), "Test email accepted by SMTP server");
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0B0E0B] border-[#1E281E] w-full sm:max-w-[1200px] overflow-y-auto" data-testid="template-sheet">
        {t && <>
          <SheetHeader><SheetTitle className="font-display text-2xl uppercase flex flex-wrap items-center gap-3">{t.number}. {t.name} <Badge s={t.status} /><span className="text-xs font-mono text-[#7C8A7C]">v{t.version} · {t.category}</span></SheetTitle></SheetHeader>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            {edit && <Btn onClick={save} data-testid="template-save-btn">Save Draft</Btn>}
            {edit && <Btn variant="ghost" onClick={dup} data-testid="template-duplicate-btn">Duplicate</Btn>}
            {can(admin, "approve_campaigns") && t.status !== "approved" && <Btn variant="ghost" onClick={approve} data-testid="template-approve-btn">Approve Template</Btn>}
            {edit && <><Input value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} w-[240px]`} data-testid="template-test-email" /><Btn variant="ghost" onClick={test} data-testid="template-test-btn">Send Test</Btn></>}
          </div>
          {t.category === "transactional" && <p className="mt-3 text-xs text-[#7C8A7C]">Transactional emails use the last approved version{t.approved_snapshot ? ` (v${t.approved_snapshot.version})` : ""}. Edits take effect once approved.</p>}
          <div className="mt-6"><EmailEditor value={t} onChange={setT} readOnly={!edit} promotional={t.category === "promotional"} /></div>
          <p className="eyebrow mt-8 mb-2">Version history</p>
          {versions.map((v) => <p key={v.id} className="text-xs text-[#A3ADA3] py-1 border-b border-[#1E281E]">v{v.version} · {fmtDateTime(v.created_at)} · {v.created_by} · {v.note}</p>)}
        </>}
      </SheetContent>
    </Sheet>
  );
};

export default function AdminTemplates() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/email/templates").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-templates">
      <PageHeader title="Email Templates" subtitle={`${items.length} editable templates · transactional and promotional`} />
      <Table testid="templates-table" rows={items} onRow={(r) => setSel(r.id)} cols={[["number", "#"], ["name", "Template"], ["subject", "Subject", (r) => <span className="text-[#A3ADA3]">{r.subject}</span>],
        ["category", "Category", (r) => <span className="capitalize">{r.category}</span>], ["status", "Status", (r) => <Badge s={r.status} />], ["version", "Version", (r) => `v${r.version}`], ["updated_at", "Updated", (r) => fmtDateTime(r.updated_at)]]} />
      <TemplateSheet id={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
